import { useState } from "react";
import { MicOff } from "lucide-react";

import DefaultProfile from "../../assets/images/default_profile_new.png";
import LiveWaveform from "../elevenlabs/ui/live-waveform";
import { REALTIME_IFRAME_CTA_BUTTON_IDS } from "../../apps/realtime";

type AutoEngagementMicScreenProps = {
  avatarUrl?: string;
  agentName?: string;
  greetingText?: string;
  primaryCtaLabel: string;
  secondaryChatCtaLabel: string;
  isSubmitting: boolean;
  fallbackChatMode: "voice" | "text";
  showScheduleLater: boolean;
  primaryCtaId: string;
  secondaryCtaId: string;
  scheduleLaterCtaId: string;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
  onScheduleLaterClick: () => void;
};

const AutoEngagementMicScreen = ({
  avatarUrl,
  agentName = "Anna",
  greetingText,
  primaryCtaLabel,
  secondaryChatCtaLabel,
  isSubmitting,
  fallbackChatMode,
  showScheduleLater,
  primaryCtaId,
  secondaryCtaId,
  scheduleLaterCtaId,
  onPrimaryClick,
  onSecondaryClick,
  onScheduleLaterClick,
}: AutoEngagementMicScreenProps): JSX.Element => {
  const [imageError, setImageError] = useState<boolean>(false);
  const resolvedGreeting = greetingText?.trim() || "How can I help you?";
  const shouldShowVoiceLoading = isSubmitting && fallbackChatMode === "voice";
  const shouldShowTextLoading = isSubmitting && fallbackChatMode === "text";

  return (
    <div className="w-full flex flex-col flex-auto items-center overflow-y-auto px-5 py-2 md:px-6">
      <div className="flex w-full max-w-[360px] flex-col items-center text-center">
        <div className="relative mt-5 md:mt-6">
          <div className="absolute -left-6 top-[52px] h-5 w-5 rounded-full bg-secondary opacity-30" />
          <div className="absolute right-0 top-0 h-5 w-5 rounded-full bg-secondary" />
          <div className="absolute right-1 bottom-2 h-4 w-4 rounded-full bg-secondary opacity-60" />
          <div className="h-[172px] w-[172px] overflow-hidden rounded-full bg-[#eef4f6] shadow-[0_8px_28px_rgba(49,102,127,0.08)] md:h-[172px] md:w-[172px]">
            <img
              src={imageError ? DefaultProfile : avatarUrl || DefaultProfile}
              alt={`${agentName} avatar`}
              className="h-full w-full object-cover"
              onError={() => {
                setImageError(true);
              }}
            />
          </div>
        </div>

        <div className="mt-4 w-full max-w-[340px] px-1">
          <LiveWaveform
            active={false}
            idlePreview
            mode="static"
            height={60}
            barWidth={4}
            barGap={4}
            barRadius={999}
            barColor="#D3D3D3"
            fadeEdges={false}
            sensitivity={1.25}
            smoothingTimeConstant={0.85}
            className="h-[74px] w-full"
          />
        </div>

        <div className="mt-4 max-w-[320px] text-primary-dark">
          <p className="m-0 text-[18px] font-semibold leading-[1.42] md:text-[20px]">
            {`Hi, I'm ${agentName}, ${resolvedGreeting}`}
          </p>
        </div>

        {/* <button
          type="button"
          id={primaryCtaId}
          className="mt-6 flex h-[48px] w-[48px] items-center justify-center rounded-full border border-primary-gray bg-white text-primary-dark shadow-[0_10px_24px_rgba(28,29,33,0.08)] transition-shadow hover:shadow-[0_14px_28px_rgba(28,29,33,0.12)] disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onPrimaryClick}
          disabled={isSubmitting}
          aria-label={primaryCtaLabel}
        >
          {shouldShowVoiceLoading ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-secondary-dark border-t-transparent" />
          ) : (
            // <MicOff className="h-7 w-7" strokeWidth={2.1} />
            
          )}
        </button> */}

       <button
        type="button"
        id={REALTIME_IFRAME_CTA_BUTTON_IDS.primary_cta}
        className="my-6 cursor-pointer bg-secondary rounded-2xl w-[250px] text-base leading-5 font-bold py-3.5 flex items-center justify-center text-white disabled:bg-secondary-dark disabled:bg-opacity-60 disabled:pointer-events-none"
        onClick={onPrimaryClick}
        disabled={isSubmitting}
      >
        <MicOff className="h-5 w-5 mr-3" strokeWidth={2} />
        Unmute to speak

      </button>

        <button
          type="button"
          id={secondaryCtaId}
          className="border-none bg-transparent text-[15px] font-medium leading-6 text-secondary-dark shadow-none disabled:cursor-not-allowed disabled:opacity-60 md:text-base"
          onClick={onSecondaryClick}
          disabled={isSubmitting}
        >
          {shouldShowTextLoading ? (
            "Loading..."
          ) : (
            <>
              {secondaryChatCtaLabel}{" "}
              <span className="underline underline-offset-4">Click here</span>
            </>
          )}
        </button>

        {showScheduleLater ? (
          <button
            type="button"
            id={scheduleLaterCtaId}
            className="mt-3 border-none bg-transparent text-[15px] font-medium leading-6 text-secondary-dark underline underline-offset-4 shadow-none disabled:cursor-not-allowed disabled:opacity-60 md:text-base"
            onClick={onScheduleLaterClick}
            disabled={isSubmitting}
          >
            Schedule call for later
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default AutoEngagementMicScreen;
